import React from "react";
import { Ban } from "lucide-react";
import OfferBadge from "./OfferBadge";
import { formatMonths, getOfferLabels } from "../../lib/offers/offerTheme";

const formatRupees = (amount) =>
  Number(amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

/**
 * What a pricing row's offer adds, e.g. "6 months + 1 month FREE". Every number
 * comes from the row's `offer` summary returned by the plans API; the only
 * arithmetic here is the display-only effective monthly price.
 *
 * `blockedPromoCode` is set when the quote said the offer does not apply because
 * of that promo code (offer.stackWithPromo === false). The panel then shows the
 * bonus as not applied and offers to remove the code instead.
 */
const PricingOfferDetails = ({ pricing, theme, isBestValue = false, onOpenTerms, blockedPromoCode = "", onRemovePromo }) => {
  const offer = pricing?.offer;
  if (!offer) return null;

  const labels = getOfferLabels(theme);
  const badgeText = offer.badgeText || `+${formatMonths(offer.bonusMonths)} FREE`;
  const totalMonths = Number(offer.totalMonths) || 0;
  const effectiveMonthly = totalMonths > 0 ? Number(pricing.price || 0) / totalMonths : 0;

  const termsButton = typeof onOpenTerms === "function" && (
    <button
      type="button"
      onClick={onOpenTerms}
      className="text-[0.82rem] font-semibold text-[#503691] underline underline-offset-2 hover:text-[#503691]/80"
    >
      Offer T&amp;C
    </button>
  );

  if (blockedPromoCode) {
    return (
      <div className="mt-3 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-3 sm:p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-2.5 py-1 text-[11px] font-black uppercase tracking-wide text-gray-500 line-through decoration-gray-400">
            <span aria-hidden="true">{theme.icon}</span>
            {badgeText}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold text-amber-800">
            <Ban size={12} aria-hidden="true" />
            Not applied
          </span>
        </div>

        <p className="mt-2 text-[0.95rem] text-gray-900">
          {formatMonths(offer.paidMonths)}{" "}
          <span className="text-gray-500 line-through">+ {formatMonths(offer.bonusMonths)} FREE</span>
        </p>
        <p className="mt-0.5 text-[0.82rem] leading-relaxed text-gray-600">
          {labels.noStackNote}. Remove <strong className="text-gray-900">{blockedPromoCode}</strong> to get{" "}
          {formatMonths(totalMonths)} of access instead of {formatMonths(offer.paidMonths)}.
        </p>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          {typeof onRemovePromo === "function" && (
            <button
              type="button"
              onClick={onRemovePromo}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[0.82rem] font-bold shadow-sm transition-transform hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{ background: theme.badgeBackground, color: theme.onAccent }}
            >
              Remove {blockedPromoCode} &amp; get {badgeText}
            </button>
          )}
          {termsButton}
        </div>
      </div>
    );
  }

  return (
    <div
      className="mt-3 rounded-xl border p-3 sm:p-4"
      style={{ borderColor: theme.accent, background: `${theme.accentSoft}1F` }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <OfferBadge theme={theme} text={badgeText} />
        {isBestValue && (
          <span className="rounded-full border border-gray-300 bg-white px-2.5 py-1 text-[11px] font-bold text-gray-800">
            {labels.bestValue}
          </span>
        )}
      </div>
      <p className="mt-2 text-[0.95rem] text-gray-900">
        {formatMonths(offer.paidMonths)}{" "}
        <strong className="font-extrabold">+ {formatMonths(offer.bonusMonths)} FREE</strong>
      </p>
      <p className="text-[0.82rem] font-medium text-gray-600">{formatMonths(totalMonths)} total access</p>
      <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
        {effectiveMonthly > 0 && (
          <span className="text-[0.82rem] font-semibold text-gray-700">
            Effective ₹{formatRupees(effectiveMonthly)}/month
          </span>
        )}
        {termsButton}
      </div>
    </div>
  );
};

export default PricingOfferDetails;
